import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Play, Pause, ChevronLeft, ChevronRight, RotateCcw, Maximize2, Minimize2, X,
  Users, Package, CreditCard, Image as ImageIcon, MessageSquare, Sparkles, Target,
  Calendar, Mic2, Handshake, TrendingUp, BarChart3, ShieldCheck, Clock, Award,
} from "lucide-react";

const TL_LOGO = "/brand/tl-logo.png";
const HQ_HERO = "/brand/tl-hq.jpeg";

// Demonstration data (clearly labeled in the UI)
const DEMO = {
  attendees: 300,
  exhibitors: 40,
  meetings: 180,
  engagement_pct: 85,
  satisfaction_pct: 90,
  pipeline_usd: 18_000_000,
  diversity_pct: 32,
  on_time_pct: 98,
  nps: 72,
  matchScore_avg: 87,
};

const SCENE_DURATION_MS = 17_000; // 17s per scene → ~2 min total

const fadeIn = (delay = 0, duration = 1.2) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -14 },
  transition: { duration, delay, ease: [0.22, 1, 0.36, 1] },
});

const sceneTransition = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 1.4, ease: [0.22, 1, 0.36, 1] },
};

export default function ExecutiveVision() {
  const nav = useNavigate();
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [isFs, setIsFs] = useState(false);
  const [toolVisible, setToolVisible] = useState(true);
  const containerRef = useRef(null);
  const toolHideTimerRef = useRef(null);
  const autoTimerRef = useRef(null);

  const scenes = useMemo(() => SCENES, []);
  const total = scenes.length;

  const goNext = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const goPrev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);
  const goRestart = useCallback(() => setIdx(0), []);

  // Auto-advance
  useEffect(() => {
    if (!playing) return;
    clearTimeout(autoTimerRef.current);
    autoTimerRef.current = setTimeout(goNext, SCENE_DURATION_MS);
    return () => clearTimeout(autoTimerRef.current);
  }, [idx, playing, goNext]);

  // Fullscreen handler
  const toggleFs = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen?.();
        setIsFs(true);
      } else {
        await document.exitFullscreen?.();
        setIsFs(false);
      }
    } catch { /* no-op */ }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Keyboard controls
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
      else if (e.key === "r" || e.key === "R") goRestart();
      else if (e.key === "f" || e.key === "F") toggleFs();
      else if (e.key === "Escape") {
        if (document.fullscreenElement) document.exitFullscreen?.();
        else nav("/");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, goRestart, toggleFs, nav]);

  // Toolbar auto-hide
  const wakeToolbar = useCallback(() => {
    setToolVisible(true);
    clearTimeout(toolHideTimerRef.current);
    toolHideTimerRef.current = setTimeout(() => setToolVisible(false), 3500);
  }, []);
  useEffect(() => {
    wakeToolbar();
    return () => clearTimeout(toolHideTimerRef.current);
  }, [wakeToolbar]);

  const scene = scenes[idx];
  const progressPct = ((idx + 1) / total) * 100;

  return (
    <div
      ref={containerRef}
      onMouseMove={wakeToolbar}
      onClick={wakeToolbar}
      className="fixed inset-0 z-50 bg-black text-white overflow-hidden select-none"
      data-testid="executive-vision-root"
    >
      {/* Demo data watermark */}
      <div className="absolute top-6 left-6 z-30 text-[10px] uppercase tracking-[0.32em] text-white/40">
        Demonstration Data
      </div>

      {/* Scene counter */}
      <div className="absolute top-6 right-6 z-30 text-[10px] uppercase tracking-[0.32em] text-white/40 tabular-nums">
        {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </div>

      {/* Scene area */}
      <AnimatePresence mode="wait">
        <motion.div
          key={scene.id}
          {...sceneTransition}
          className="absolute inset-0"
        >
          {scene.render({ idx, total })}
        </motion.div>
      </AnimatePresence>

      {/* Progress bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: toolVisible ? 1 : 0 }}
        transition={{ duration: 0.6 }}
        className="absolute bottom-0 inset-x-0 h-px bg-white/10 z-30"
      >
        <motion.div
          key={`${idx}-${playing}`}
          initial={{ width: `${(idx / total) * 100}%` }}
          animate={{ width: `${progressPct}%` }}
          transition={{ duration: SCENE_DURATION_MS / 1000, ease: "linear" }}
          className="h-full bg-[#A39171]"
        />
      </motion.div>

      {/* Presenter toolbar */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: toolVisible ? 1 : 0, y: toolVisible ? 0 : 12 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="absolute bottom-6 inset-x-0 flex justify-center z-30 pointer-events-none"
      >
        <div className="pointer-events-auto flex items-center gap-1 bg-black/80 backdrop-blur-md border border-white/10 rounded-full px-2 py-2 shadow-2xl">
          <ToolBtn onClick={goPrev} label="Previous" testId="vision-prev"><ChevronLeft size={16} /></ToolBtn>
          <ToolBtn onClick={() => setPlaying((p) => !p)} label={playing ? "Pause" : "Play"} testId="vision-play">
            {playing ? <Pause size={16} /> : <Play size={16} />}
          </ToolBtn>
          <ToolBtn onClick={goNext} label="Next" testId="vision-next"><ChevronRight size={16} /></ToolBtn>
          <div className="h-5 w-px bg-white/15 mx-1" />
          <ToolBtn onClick={goRestart} label="Restart" testId="vision-restart"><RotateCcw size={14} /></ToolBtn>
          <ToolBtn onClick={toggleFs} label={isFs ? "Exit Fullscreen" : "Fullscreen"} testId="vision-fs">
            {isFs ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </ToolBtn>
          <div className="h-5 w-px bg-white/15 mx-1" />
          <Link to="/" className="h-9 w-9 inline-flex items-center justify-center text-white/70 hover:text-white" aria-label="Exit" data-testid="vision-exit">
            <X size={16} />
          </Link>
        </div>
      </motion.div>

      {/* Scene title chip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: toolVisible ? 1 : 0 }}
        transition={{ duration: 0.6 }}
        className="absolute bottom-6 left-6 z-30 text-[10px] uppercase tracking-[0.32em] text-white/55"
      >
        {scene.label}
      </motion.div>
    </div>
  );
}

function ToolBtn({ onClick, label, children, testId }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
      className="h-9 w-9 inline-flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
    >
      {children}
    </button>
  );
}

/* ===========================================================
   SCENES
   =========================================================== */

const SCENES = [
  {
    id: "opening",
    label: "Scene 01 · Opening",
    render: () => (
      <div className="absolute inset-0">
        {/* Hero image with slow Ken Burns */}
        <motion.img
          src={HQ_HERO}
          alt="Travel + Leisure Headquarters"
          initial={{ scale: 1.04 }}
          animate={{ scale: 1.16 }}
          transition={{ duration: SCENE_DURATION_MS / 1000, ease: "linear" }}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 35%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/20 to-transparent" />

        <div className="relative h-full flex flex-col items-start justify-end pb-32 px-10 sm:px-16 md:px-24">
          <motion.img
            src={TL_LOGO}
            alt="Travel + Leisure"
            {...fadeIn(0.5, 1.4)}
            style={{ filter: "invert(1)", mixBlendMode: "screen" }}
            className="h-16 sm:h-20 w-auto self-start"
          />
          <motion.h1 {...fadeIn(1.5, 1.4)}
            className="serif text-5xl sm:text-7xl lg:text-8xl leading-[1.02] mt-10 max-w-5xl tracking-tight text-white">
            Supplier <span className="text-[#A39171]">Engagement</span> Platform
          </motion.h1>
          <motion.div {...fadeIn(2.6, 1.2)} className="mt-10 space-y-1 text-base sm:text-lg text-white/85">
            <div className="serif text-2xl sm:text-3xl">Strategic Supplier Summit 2026</div>
            <div className="text-sm text-white/70 tracking-wide mt-3">Friday, September 11, 2026</div>
            <div className="text-sm text-white/70 tracking-wide">Travel + Leisure Headquarters</div>
            <div className="text-sm text-white/70 tracking-wide">501 W. Church Street · Orlando, Florida</div>
          </motion.div>
          <motion.p {...fadeIn(3.8, 1.2)}
            className="mt-10 max-w-2xl text-base sm:text-lg italic serif text-white/85 leading-relaxed">
            "Connecting Procurement Leaders, Strategic Suppliers, and Business Innovation."
          </motion.p>
        </div>
      </div>
    ),
  },
  {
    id: "pre-event",
    label: "Scene 02 · Pre-Event",
    render: () => (
      <SceneFrame
        accent="pre"
        eyebrow="Phase 01 · Pre-Event"
        headline={<>Everything begins <em className="not-italic text-[#A39171]">before</em> the event.</>}
        body="From the first registration to the final logistics email — the platform orchestrates every supplier touchpoint."
        tiles={[
          { icon: Users, title: "Exhibitor Registration", body: "A streamlined intake — company, package, certifications, artwork." },
          { icon: Package, title: "Supplier Packages", body: "Strategic Supplier Package at $2,500 — all-inclusive." },
          { icon: CreditCard, title: "Stripe Payment", body: "Secure checkout with automatic payment-status tracking." },
          { icon: ImageIcon, title: "Artwork Upload", body: "Logos, backwall graphics, and capability statements — versioned." },
          { icon: MessageSquare, title: "Communications", body: "Eleven scheduled emails — T-30, T-14, T-7, T-3, day-of, and follow-ups." },
          { icon: ShieldCheck, title: "Admin Approvals", body: "Display America reviews and approves before public listing." },
        ]}
      />
    ),
  },
  {
    id: "connect",
    label: "Scene 03 · Connect",
    render: () => (
      <SceneFrame
        accent="connect"
        eyebrow="Phase 02 · Discovery"
        headline={<>Connecting the right suppliers with the <em className="not-italic text-[#A39171]">right</em> opportunities.</>}
        body="A live directory, AI-powered matchmaking, and 14 strategic procurement categories — built around how Travel + Leisure actually sources."
        tiles={[
          { icon: Users, title: "Supplier Directory", body: `${DEMO.exhibitors} approved suppliers · filterable by industry, certification, and category.` },
          { icon: Sparkles, title: "AI Matchmaking", body: `Personalized recommendations across 7 dimensions · avg Match Score ${DEMO.matchScore_avg}%.` },
          { icon: Target, title: "Procurement Opportunities", body: "14 categories from Construction to Travel — each with owner, sourcing priorities, and live RFPs." },
        ]}
      />
    ),
  },
  {
    id: "engage",
    label: "Scene 04 · Engage",
    render: () => (
      <SceneFrame
        accent="engage"
        eyebrow="Phase 03 · Engagement"
        headline={<>Designed for <em className="not-italic text-[#A39171]">meaningful</em> engagement.</>}
        body="A curated program built around procurement conversations — every minute on the floor has a purpose."
        tiles={[
          { icon: Calendar, title: "Executive Schedule", body: "Welcome, opening remarks, fireside chat, breakout tracks, networking reception." },
          { icon: Mic2, title: "Speakers", body: "Procurement leaders, category owners, supplier diversity champions." },
          { icon: Handshake, title: "Procurement Meetings", body: `${DEMO.meetings} scheduled · 1:1 conversations between buyers and suppliers.` },
          { icon: BarChart3, title: "Breakout Sessions", body: "Doing business with T+L · Innovation · Risk & Sustainability · Prime engagement." },
        ]}
      />
    ),
  },
  {
    id: "outcomes",
    label: "Scene 05 · Outcomes",
    render: () => (
      <SceneFrame
        accent="outcomes"
        eyebrow="Phase 04 · Outcomes"
        headline={<>Business impact doesn't end when the <em className="not-italic text-[#A39171]">event</em> ends.</>}
        body="Every supplier gets a personal Success Dashboard answering the question their CFO will ask: was the $2,500 worth it?"
        kpis={[
          { value: 82, suffix: "/100", label: "Avg Lead Score" },
          { value: "Highly Recommended", label: "2027 Renewal Recommendation" },
          { value: DEMO.meetings, label: "Meetings Completed" },
          { value: 4.3, suffix: "/5", label: "Avg Lead Quality" },
        ]}
        tiles={[
          { icon: TrendingUp, title: "Business Match Score", body: "Score per category surfaces the highest-yield Travel + Leisure conversations." },
          { icon: Award, title: "Lead Score", body: "Composite 0–100 across views, QR scans, meetings, and lead quality." },
          { icon: Clock, title: "Renewal Recommendation", body: "Auto-generated · 'Highly Recommended', 'Recommended', or 'Needs Attention'." },
        ]}
      />
    ),
  },
  {
    id: "command",
    label: "Scene 06 · Command",
    render: () => (
      <SceneFrame
        accent="command"
        eyebrow="Phase 05 · Executive Command"
        headline={<>Executive visibility in <em className="not-italic text-[#A39171]">real time</em>.</>}
        body="One screen the CPO can leave open all day — the operational pulse of the entire summit."
        kpis={[
          { value: DEMO.attendees, label: "Attendees" },
          { value: DEMO.exhibitors, label: "Exhibitors" },
          { value: DEMO.meetings, label: "Meetings" },
          { value: DEMO.engagement_pct, suffix: "%", label: "Attendee Engagement" },
          { value: DEMO.satisfaction_pct, suffix: "%", label: "Exhibitor Satisfaction" },
          { value: DEMO.diversity_pct, suffix: "%", label: "Supplier Diversity" },
        ]}
        rightStack={[
          { label: "Estimated Supplier Opportunities", value: "$18M" },
          { label: "Operations On-Time", value: `${DEMO.on_time_pct}%` },
          { label: "Net Promoter Score", value: DEMO.nps },
        ]}
      />
    ),
  },
  {
    id: "closing",
    label: "Scene 07 · Closing",
    render: () => (
      <div className="absolute inset-0">
        <motion.img
          src={HQ_HERO}
          alt=""
          initial={{ scale: 1.05 }}
          animate={{ scale: 1.18 }}
          transition={{ duration: SCENE_DURATION_MS / 1000, ease: "linear" }}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 35%" }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/45 to-black/85" />

        <div className="relative h-full flex flex-col items-center justify-center text-center px-8 text-white">
          <motion.img
            src={TL_LOGO}
            alt="Travel + Leisure"
            {...fadeIn(0.4, 1.4)}
            style={{ filter: "invert(1)", mixBlendMode: "screen" }}
            className="h-16 sm:h-20 w-auto"
          />
          <motion.div {...fadeIn(1.8, 1.5)} className="mt-16 serif text-5xl sm:text-7xl leading-[1.05] max-w-4xl text-white">
            One Partner.<br />
            One Platform.<br />
            <span className="text-[#A39171]">One Exceptional</span> Supplier Experience.
          </motion.div>
          <motion.div {...fadeIn(4.2, 1.2)} className="mt-20 flex items-center gap-4 text-[11px] uppercase tracking-[0.36em] text-white/70">
            <span className="h-px w-12 bg-[#A39171]" />
            Produced by Display America
            <span className="h-px w-12 bg-[#A39171]" />
          </motion.div>
        </div>
      </div>
    ),
  },
];

/* ===========================================================
   Reusable scene frame for content scenes
   =========================================================== */

function SceneFrame({ eyebrow, headline, body, tiles = [], kpis = [], rightStack = [], kpisCols = 2 }) {
  const kpiGridClass = kpisCols === 3 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2";
  return (
    <div className="absolute inset-0 bg-[#050505]">
      {/* Background ambient texture — subtle */}
      <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_20%_30%,_#A39171_0%,_transparent_50%)]" />
      <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_80%_70%,_#A39171_0%,_transparent_55%)]" />

      <div className="relative h-full grid lg:grid-cols-[1.05fr_1fr] gap-12 px-10 sm:px-16 md:px-24 py-24">
        {/* Left — headline */}
        <div className="flex flex-col justify-center">
          <motion.div {...fadeIn(0.4, 1.0)} className="overline text-zinc-400 mb-8">
            <span className="accent-rule" />{eyebrow}
          </motion.div>
          <motion.h2 {...fadeIn(0.9, 1.3)} className="serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            {headline}
          </motion.h2>
          {body && (
            <motion.p {...fadeIn(1.8, 1.0)} className="mt-10 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed">
              {body}
            </motion.p>
          )}
          {rightStack.length > 0 && (
            <motion.div {...fadeIn(2.6, 1.0)} className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-6">
              {rightStack.map((r) => (
                <div key={r.label}>
                  <div className="serif text-3xl sm:text-4xl tabular-nums">{r.value}</div>
                  <div className="overline mt-2 text-zinc-400">{r.label}</div>
                </div>
              ))}
            </motion.div>
          )}
        </div>

        {/* Right — tiles or KPIs */}
        <div className="flex flex-col justify-center gap-6">
          {kpis.length > 0 && (
            <div className={`grid ${kpiGridClass} gap-px bg-white/10`}>
              {kpis.map((k, i) => (
                <motion.div
                  key={k.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 + i * 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-[#0A0A0A] p-6"
                >
                  <div className="serif text-4xl sm:text-5xl tabular-nums leading-none">
                    {typeof k.value === "number" ? k.value.toLocaleString() : k.value}
                    {k.suffix && <span className="text-2xl sm:text-3xl text-white/60">{k.suffix}</span>}
                  </div>
                  <div className="overline mt-4 text-zinc-400">{k.label}</div>
                </motion.div>
              ))}
            </div>
          )}
          {tiles.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-px bg-white/10">
              {tiles.map((t, i) => (
                <motion.div
                  key={t.title}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.6 + i * 0.18, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-[#0A0A0A] p-6"
                >
                  <t.icon size={18} strokeWidth={1.4} className="text-[#A39171]" />
                  <div className="serif text-xl mt-4">{t.title}</div>
                  <p className="text-sm text-white/65 mt-2 leading-relaxed">{t.body}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
